// VCIN Brand: Always use VCIN_COLORS from constants.js - never use Tailwind blue classes
import React, { useState, useEffect } from 'react';
import {
  Building2, Phone, MapPin, Mail, Calendar, User, Smartphone,
  AlertCircle, Loader2, Check, Edit2, X, Lock, Eye, EyeOff
} from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import api from '../../services/api';
import { VCIN_COLORS } from '../../styles/constants';

// Subscription Badge - uses VCIN brand blue for Standard tier
function SubscriptionBadge({ tier }) {
  const config = {
    STANDARD: { bg: `${VCIN_COLORS.primary}15`, text: VCIN_COLORS.primary, label: 'Standard' },
    PROFESSIONAL: { bg: '#E8F5E9', text: '#2E7D32', label: 'Professional' },
    ENTERPRISE: { bg: '#FFF3E0', text: '#E65100', label: 'Enterprise' }
  };
  const style = config[tier] || config.STANDARD;
  
  return (
    <span 
      className="px-3 py-1 rounded-full text-sm font-semibold"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {style.label}
    </span>
  );
}

// Loading Skeleton
function AccountSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="h-6 w-40 bg-gray-200 rounded mb-6" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex items-center gap-4 py-4 border-b border-gray-100">
              <div className="w-10 h-10 bg-gray-200 rounded-lg" />
              <div className="flex-1">
                <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
                <div className="h-5 w-48 bg-gray-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Editable Field Component
function EditableField({ icon: Icon, label, value, fieldName, isEditing, editValue, onChange, type = 'text', placeholder }) {
  return (
    <div className="flex items-start gap-4 py-4 border-b border-gray-100 last:border-0">
      <div 
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${VCIN_COLORS.primary}10` }}
      >
        <Icon className="w-5 h-5" style={{ color: VCIN_COLORS.primary }} />
      </div>
      <div className="flex-1">
        <p className="text-sm text-gray-500 mb-1">{label}</p>
        {isEditing ? (
          <input
            type={type}
            value={editValue}
            onChange={(e) => onChange(fieldName, e.target.value)}
            className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
            placeholder={placeholder}
            data-testid={`edit-${fieldName}`}
          />
        ) : (
          <p className="text-gray-900 font-medium">{value || '-'}</p>
        )}
      </div>
    </div>
  );
}

// Success Message Component
function SuccessMessage({ message }) {
  return (
    <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
      <Check className="w-4 h-4" />
      {message}
    </div>
  );
}

export default function AccountPage() {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Edit states for each section
  const [editingCompany, setEditingCompany] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  
  // Form data for editing
  const [companyForm, setCompanyForm] = useState({ name: '', address: '' });
  const [contactForm, setContactForm] = useState({ contact_name: '', contact_mobile: '', contact_email: '' });
  const [phoneForm, setPhoneForm] = useState({ phone_number: '' });
  
  // Save states
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);
  
  // Password change states
  const { user: clerkUser } = useUser();
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        setLoading(true);
        const response = await api.get('/account');
        const c = response.data.company;
        setCompany(c);
        setCompanyForm({ name: c.name || '', address: c.address || '' });
        setContactForm({ 
          contact_name: c.contact_name || '', 
          contact_mobile: c.contact_mobile || '', 
          contact_email: c.contact_email || '' 
        });
        setPhoneForm({ phone_number: c.phone_number || '' });
      } catch (err) {
        setError(err.message || 'Failed to load account details');
      } finally {
        setLoading(false);
      }
    };
    fetchAccount();
  }, []);

  const handleSaveSection = async (section, data) => {
    try {
      setSaving(true);
      await api.patch('/account', data);
      setCompany(prev => ({ ...prev, ...data }));
      
      if (section === 'company') setEditingCompany(false);
      if (section === 'contact') setEditingContact(false);
      if (section === 'phone') setEditingPhone(false);
      
      setSaveSuccess(section);
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err) {
      alert(err.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = (section) => {
    if (section === 'company') {
      setEditingCompany(false);
      setCompanyForm({ name: company?.name || '', address: company?.address || '' });
    }
    if (section === 'contact') {
      setEditingContact(false);
      setContactForm({ 
        contact_name: company?.contact_name || '', 
        contact_mobile: company?.contact_mobile || '', 
        contact_email: company?.contact_email || '' 
      });
    }
    if (section === 'phone') {
      setEditingPhone(false);
      setPhoneForm({ phone_number: company?.phone_number || '' });
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    
    if (!passwordForm.current || !passwordForm.new || !passwordForm.confirm) {
      setPasswordError('All fields are required');
      return;
    }
    if (passwordForm.new.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (passwordForm.new !== passwordForm.confirm) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    try {
      setPasswordSaving(true);
      await clerkUser.updatePassword({
        currentPassword: passwordForm.current,
        newPassword: passwordForm.new
      });
      setPasswordSuccess(true);
      setPasswordForm({ current: '', new: '', confirm: '' });
      setTimeout(() => setPasswordSuccess(false), 5000);
    } catch (err) {
      const e0 = err.errors?.[0];
      setPasswordError(e0?.longMessage || e0?.message || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  if (loading) return <AccountSkeleton />;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <span className="text-red-700">{error}</span>
      </div>
    );
  }

  return (
    <div data-testid="account-page" className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Account</h1>

      {/* Company Details Section */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Company Details</h2>
            <SubscriptionBadge tier={company?.subscription_tier} />
          </div>
          {!editingCompany ? (
            <button
              onClick={() => setEditingCompany(true)}
              className="flex items-center gap-1 text-sm font-medium hover:opacity-80"
              style={{ color: VCIN_COLORS.primary }}
              data-testid="edit-company-btn"
            >
              <Edit2 className="w-4 h-4" /> Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSaveSection('company', companyForm)}
                disabled={saving}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: VCIN_COLORS.primary }}
                data-testid="save-company-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
              </button>
              <button
                onClick={() => handleCancelEdit('company')}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                <X className="w-4 h-4" /> Cancel
              </button>
            </div>
          )}
        </div>
        
        {saveSuccess === 'company' && <SuccessMessage message="Company details saved successfully" />}
        
        <div className="divide-y divide-gray-100">
          <EditableField 
            icon={Building2} 
            label="Company Name" 
            value={company?.name}
            fieldName="name"
            isEditing={editingCompany}
            editValue={companyForm.name}
            onChange={(field, value) => setCompanyForm(prev => ({ ...prev, [field]: value }))}
            placeholder="Enter company name"
          />
          <EditableField 
            icon={MapPin} 
            label="Address" 
            value={company?.address}
            fieldName="address"
            isEditing={editingCompany}
            editValue={companyForm.address}
            onChange={(field, value) => setCompanyForm(prev => ({ ...prev, [field]: value }))}
            placeholder="Enter full address"
          />
          <div className="flex items-start gap-4 py-4">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${VCIN_COLORS.primary}10` }}
            >
              <Calendar className="w-5 h-5" style={{ color: VCIN_COLORS.primary }} />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-500 mb-1">Member Since</p>
              <p className="text-gray-900 font-medium">
                {company?.created_at ? new Date(company.created_at).toLocaleDateString('en-GB', {
                  day: '2-digit', month: 'long', year: 'numeric'
                }) : '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Details Section */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Contact Details</h2>
          {!editingContact ? (
            <button
              onClick={() => setEditingContact(true)}
              className="flex items-center gap-1 text-sm font-medium hover:opacity-80"
              style={{ color: VCIN_COLORS.primary }}
              data-testid="edit-contact-btn"
            >
              <Edit2 className="w-4 h-4" /> Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSaveSection('contact', contactForm)}
                disabled={saving}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: VCIN_COLORS.primary }}
                data-testid="save-contact-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
              </button>
              <button
                onClick={() => handleCancelEdit('contact')}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                <X className="w-4 h-4" /> Cancel
              </button>
            </div>
          )}
        </div>
        
        {saveSuccess === 'contact' && <SuccessMessage message="Contact details saved successfully" />}
        
        <div className="divide-y divide-gray-100">
          <EditableField 
            icon={User} 
            label="Contact Name" 
            value={company?.contact_name}
            fieldName="contact_name"
            isEditing={editingContact}
            editValue={contactForm.contact_name}
            onChange={(field, value) => setContactForm(prev => ({ ...prev, [field]: value }))}
            placeholder="Primary contact name"
          />
          <EditableField 
            icon={Smartphone} 
            label="Contact Mobile" 
            value={company?.contact_mobile}
            fieldName="contact_mobile"
            isEditing={editingContact}
            editValue={contactForm.contact_mobile}
            onChange={(field, value) => setContactForm(prev => ({ ...prev, [field]: value }))}
            type="tel"
            placeholder="07XXX XXXXXX"
          />
          <EditableField 
            icon={Mail} 
            label="Contact Email" 
            value={company?.contact_email}
            fieldName="contact_email"
            isEditing={editingContact}
            editValue={contactForm.contact_email}
            onChange={(field, value) => setContactForm(prev => ({ ...prev, [field]: value }))}
            type="email"
            placeholder="contact@company.co.uk"
          />
        </div>
      </div>

      {/* Main Office Phone Section */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Main Office Phone Number</h2>
          {!editingPhone ? (
            <button
              onClick={() => setEditingPhone(true)}
              className="flex items-center gap-1 text-sm font-medium hover:opacity-80"
              style={{ color: VCIN_COLORS.primary }}
              data-testid="edit-phone-btn"
            >
              <Edit2 className="w-4 h-4" /> Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSaveSection('phone', phoneForm)}
                disabled={saving}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: VCIN_COLORS.primary }}
                data-testid="save-phone-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
              </button>
              <button
                onClick={() => handleCancelEdit('phone')}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                <X className="w-4 h-4" /> Cancel
              </button>
            </div>
          )}
        </div>
        
        {saveSuccess === 'phone' && <SuccessMessage message="Phone number saved successfully" />}
        
        <div className="flex items-start gap-4">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${VCIN_COLORS.primary}10` }}
          >
            <Phone className="w-5 h-5" style={{ color: VCIN_COLORS.primary }} />
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-500 mb-1">Phone Number</p>
            {editingPhone ? (
              <input
                type="tel"
                value={phoneForm.phone_number}
                onChange={(e) => setPhoneForm({ phone_number: e.target.value })}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                placeholder="020 XXXX XXXX"
                data-testid="edit-phone_number"
              />
            ) : (
              <p className="text-gray-900 font-medium">{company?.phone_number || 'Not set'}</p>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-4 ml-14">
          This number is displayed to officers when they encounter high-risk vehicles.
        </p>
      </div>

      {/* Change Password Section */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h2>
        
        {passwordSuccess && <SuccessMessage message="Password changed successfully" />}
        
        {passwordError && (
          <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {passwordError}
          </div>
        )}
        
        <div className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <div className="relative">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordForm.current}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, current: e.target.value }))}
                className="w-full h-10 px-3 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                placeholder="Enter current password"
                data-testid="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={passwordForm.new}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, new: e.target.value }))}
                className="w-full h-10 px-3 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                placeholder="Enter new password"
                data-testid="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <input
              type="password"
              value={passwordForm.confirm}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
              className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
              placeholder="Confirm new password"
              data-testid="confirm-password"
            />
          </div>
          
          <button
            onClick={handleChangePassword}
            disabled={passwordSaving}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: VCIN_COLORS.primary }}
            data-testid="change-password-btn"
          >
            {passwordSaving ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Changing...</>
            ) : (
              <><Lock className="w-4 h-4" /> Change Password</>
            )}
          </button>
        </div>
      </div>

      {/* Support Note */}
      <p className="text-sm text-gray-500 text-center py-4">
        For help or support with VCIN, contact us at{' '}
        <a href="mailto:onboarding@vcin.co.uk" className="underline hover:text-gray-700">
          onboarding@vcin.co.uk
        </a>
      </p>
    </div>
  );
}
